"use client";
import React, { useState, useRef, useEffect } from "react";
import BlogCard from "~/components/reusables/blog-card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { cn } from "~/lib/utils";
import type { BlogAuthorBlog, BlogAuthorSortOption } from "~/lib/blog-author-types";

const BLOGS_PER_PAGE = 3;

const getPageStorageKey = (authorKey: string) =>
  `blog-author-current-page:${authorKey}`;

export const BlogAuthorList = ({
  blogs,
  authorKey,
}: {
  blogs?: BlogAuthorBlog[];
  authorKey: string;
}) => {
  const blogListRef = useRef<HTMLDivElement>(null);
  const shouldScrollOnPageChangeRef = useRef(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState<BlogAuthorSortOption>("Latest");
  const prevSortOptionRef = useRef(sortOption);
  const authorBlogs = blogs ?? [];

  useEffect(() => {
    const initialPage = parseInt(
      localStorage.getItem(getPageStorageKey(authorKey)) || "1",
      10,
    );
    const max = Math.ceil(authorBlogs.length / BLOGS_PER_PAGE);
    if (initialPage >= 1 && initialPage <= max) {
      setCurrentPage(initialPage);
    } else {
      setCurrentPage(1);
    }
  }, [authorKey, authorBlogs.length]);

  const sortBlogs = (
    blogsToSort: BlogAuthorBlog[],
    option: BlogAuthorSortOption,
  ) =>
    [...blogsToSort].toSorted((a, b) => {
      const dateA = new Date(a.publishDate ?? 0).getTime();
      const dateB = new Date(b.publishDate ?? 0).getTime();
      return option === "Latest" ? dateB - dateA : dateA - dateB;
    });

  const sortedBlogs = sortBlogs(authorBlogs, sortOption);
  const totalPages = Math.ceil(authorBlogs.length / BLOGS_PER_PAGE);
  const indexOfLastBlog = currentPage * BLOGS_PER_PAGE;
  const indexOfFirstBlog = indexOfLastBlog - BLOGS_PER_PAGE;
  const currentBlogs = sortedBlogs.slice(indexOfFirstBlog, indexOfLastBlog);

  const paginate = (pageNumber: number) => {
    shouldScrollOnPageChangeRef.current = true;
    setCurrentPage(pageNumber);
    if (typeof window !== "undefined") {
      localStorage.setItem(
        getPageStorageKey(authorKey),
        pageNumber.toString(),
      );
    }
  };

  useEffect(() => {
    if (prevSortOptionRef.current === sortOption) return;

    prevSortOptionRef.current = sortOption;
    setCurrentPage(1);
    if (typeof window !== "undefined") {
      localStorage.setItem(getPageStorageKey(authorKey), "1");
    }
  }, [sortOption, authorKey]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!shouldScrollOnPageChangeRef.current) return;
    shouldScrollOnPageChangeRef.current = false;
    blogListRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentPage]);

  return (
    <div ref={blogListRef} className="container relative my-[60px] lg:my-[100px] pt-[60px] md:pt-[100px]">
      {/* Decorative Bracket */}
      <div className="absolute -top-4 left-4 md:left-6 lg:left-8 h-[calc(100%+16px)] w-[1px] bg-[#9345E1]/60"></div>
      <div className="absolute top-0 left-0 w-[calc(100%+20px)] md:w-[calc(100%+40px)] lg:w-[calc(100%+60px)] h-[1px] bg-[linear-gradient(to_right,#9345E1,#2979C7,#05BAD1,#00C4D3,#F5B35C,#EB7251,#C33A5F)] opacity-80"></div>
      <div className="absolute top-0 left-4 md:left-6 lg:left-8 w-[10px] h-[10px] bg-black transform rotate-45 -translate-x-1/2 -translate-y-1/2 hidden md:block"></div>

      <div className="pl-6 md:pl-[30px] lg:pl-[40px] xl:pl-[60px] relative mb-[50px]">
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6 mb-[40px] md:mb-[60px] pr-6 md:pr-[60px]">
          <h2 className="text-4xl md:text-5xl lg:text-[60px] font-normal leading-tight text-black flex-shrink-0">
            Blogs from this author
          </h2>
          <div className="z-10 flex-shrink-0 w-full md:w-[300px]">
            <Select
              value={sortOption}
              onValueChange={(value) =>
                setSortOption(value as BlogAuthorSortOption)
              }
            >
              <SelectTrigger className="w-full h-[50px] border-black text-black bg-white">
                <SelectValue placeholder="Latest" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="Latest">Latest</SelectItem>
                <SelectItem value="Earliest">Earliest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {authorBlogs.length ? (
          <div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {currentBlogs.map((blog) => (
                <div
                  key={blog.key ?? `${blog.publishDate}-${blog.title}`}
                  className="relative min-h-[380px]"
                >
                  <BlogCard blog={blog} size="md" imageAlign="left" />
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-16 flex justify-center">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    aria-label="Previous page"
                    disabled={currentPage === 1}
                    onClick={() => paginate(currentPage - 1)}
                    className={cn(
                      "mr-2 text-black transition-colors hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline",
                    )}
                  >
                    &lt; Prev
                  </button>
                  {[...Array(totalPages).keys()].map((number) => (
                    <button
                      key={number}
                      type="button"
                      aria-label={`Page ${number + 1}`}
                      aria-current={
                        currentPage === number + 1 ? "page" : undefined
                      }
                      onClick={() => paginate(number + 1)}
                      className={cn(
                        "flex h-10 w-10 items-center justify-center rounded-md border border-black text-sm transition-colors",
                        currentPage === number + 1
                          ? "border-transparent bg-gradient-pagination text-white"
                          : "bg-white text-black hover:bg-gradient-blue-purple hover:text-white",
                      )}
                    >
                      {number + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    aria-label="Next page"
                    disabled={currentPage === totalPages}
                    onClick={() => paginate(currentPage + 1)}
                    className={cn(
                      "ml-2 text-black transition-colors hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline",
                    )}
                  >
                    Next &gt;
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <h4 className="font-h4 text-gray-500">No results found.</h4>
          </div>
        )}
      </div>
    </div>
  );
};
